/**
 * ClientsScreen — gestión de clientes (entidad primaria desde B1).
 *
 * Layout:
 *   - Header: título + botón "+ Nuevo cliente" + indicador de uso vs cap del tier.
 *   - Toolbar: search debounced (250ms) + tabs status (Activos / Archivados / Todos).
 *   - Grid: cards con avatar (foto o iniciales), datos básicos, plans_count y
 *           acciones quick (editar, archivar/restaurar, ver planes).
 *   - Empty: si 0 activos, CTA enorme "Crea tu primer cliente". Si filtro vacío
 *           "Sin resultados".
 *
 * Hard-wall:
 *   - El botón "+ Nuevo cliente" se deshabilita si el tier llegó a cap.
 *   - Si igualmente createClient devuelve 402 (race condition u otro caller),
 *     el ClientFormModal muestra banner inline con CTA upgrade.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../lib/toast";
import { Icon } from "../lib/Icon";
import { Avatar, EmptyState } from "../lib/components";
import { getUsage, type UsageSummary } from "../api/auth";
import {
  archiveClient,
  listClients,
  restoreClient,
  type ClientFull,
  type ClientStatusFilter,
} from "../api/clients";
import { ClientFormModal } from "./clients/ClientFormModal";
import type { Route } from "../types";
import { CreatePlanModal } from "./plan/CreatePlanModal";

interface ClientsScreenProps {
  go: (r: Route, query?: string) => void;
}

function relativeTime(iso?: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 60) return `hace ${Math.max(1, min)} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 7) return `hace ${d} d`;
  return `hace ${Math.round(d / 7)} sem`;
}

function initialsOf(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const PALETTE = ["#7c3aed", "#ec4899", "#059669", "#2563eb", "#f59e0b", "#dc2626", "#0891b2"];
function colorFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % PALETTE.length;
  return PALETTE[h];
}

export function ClientsScreen({ go }: ClientsScreenProps) {
  const { user, refreshUser } = useAuth();
  const toast = useToast();

  // Datos
  const [clients, setClients] = useState<ClientFull[] | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<ClientStatusFilter>("active");
  /** Texto que se está tipeando — alimenta el debounce. */
  const [qInput, setQInput] = useState("");
  /** Texto debounced que va al backend. */
  const [qDebounced, setQDebounced] = useState("");

  // Modal
  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [editing, setEditing] = useState<ClientFull | null>(null);
  /** Cliente para el que se está abriendo el modal de "Crear plan" — el flujo
   *  unificado le entrega este cliente como lockedClient así no se puede cambiar. */
  const [creatingPlanFor, setCreatingPlanFor] = useState<ClientFull | null>(null);

  // Debounce 250ms para qInput → qDebounced.
  useEffect(() => {
    const t = window.setTimeout(() => setQDebounced(qInput.trim()), 250);
    return () => window.clearTimeout(t);
  }, [qInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [list, u] = await Promise.all([
        listClients({ status, q: qDebounced || undefined }),
        getUsage(),
      ]);
      setClients(list);
      setUsage(u);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [status, qDebounced]);

  useEffect(() => {
    load();
  }, [load]);

  const limitReached = usage
    ? !usage.is_unlimited && usage.clients_limit >= 0 && usage.clients_active >= usage.clients_limit
    : false;

  // Handlers de acciones de card

  const handleArchive = useCallback(
    async (c: ClientFull) => {
      try {
        const saved = await archiveClient(c.id);
        toast(`"${saved.full_name}" archivado`);
        await Promise.all([load(), refreshUser()]);
      } catch (e) {
        toast("No pude archivar: " + (e instanceof Error ? e.message : ""));
      }
    },
    [load, refreshUser, toast],
  );

  const handleRestore = useCallback(
    async (c: ClientFull) => {
      try {
        const saved = await restoreClient(c.id);
        toast(`"${saved.full_name}" restaurado`);
        await Promise.all([load(), refreshUser()]);
      } catch (e) {
        // Si es 402, abrimos el modal de edición que ya muestra el banner limit
        // (UX: el coach ve el detalle exacto sin perder contexto)
        toast("No pude restaurar: " + (e instanceof Error ? e.message : ""));
        setEditing(c);
        setModalMode("edit");
      }
    },
    [load, refreshUser, toast],
  );

  const openCreate = () => {
    setEditing(null);
    setModalMode("create");
  };

  const openEdit = (c: ClientFull) => {
    setEditing(c);
    setModalMode("edit");
  };

  // Empty state messaging
  const isEmpty = (clients?.length ?? 0) === 0;
  const isFiltered = qDebounced.length > 0 || status !== "active";

  if (loading && !clients) {
    return (
      <div className="page" style={{ paddingTop: 24 }}>
        <div className="muted">Cargando clientes…</div>
      </div>
    );
  }

  if (err && !clients) {
    return (
      <div className="page" style={{ paddingTop: 48 }}>
        <EmptyState
          icon="user"
          title="Algo salió mal"
          body={err}
          action={
            <button className="btn btn-primary" onClick={load}>
              Reintentar
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="page al-cli">
      {/* ---------- Header ---------- */}
      <div className="al-cli-head">
        <div>
          <h1 className="al-page-title">Clientes</h1>
          <p className="muted">
            {usage
              ? usage.is_unlimited || usage.clients_limit < 0
                ? `${usage.clients_active} cliente${usage.clients_active === 1 ? "" : "s"} activos · ilimitado`
                : `${usage.clients_active} de ${usage.clients_limit} clientes activos`
              : "—"}
            {user?.plan_tier && (
              <>
                {" · "}
                <span className="al-cli-tier-chip">{usage?.plan_label || user.plan_tier}</span>
              </>
            )}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={openCreate}
          disabled={limitReached}
          title={limitReached ? "Has alcanzado el límite de tu plan" : "Crear cliente"}
        >
          <Icon name="plus" size={14} /> Nuevo cliente
        </button>
      </div>

      {limitReached && (
        <div className="al-cli-banner">
          <div>
            <b>Has alcanzado tu límite.</b>{" "}
            <span className="muted">
              Tu {usage?.plan_label} permite hasta {usage?.clients_limit} clientes activos.
              Archiva uno o haz upgrade para añadir más.
            </span>
          </div>
          <a
            className="btn btn-secondary btn-sm"
            href="mailto:nilton.alfonso@nilabs.co?subject=Upgrade%20AetherLite"
          >
            Ver opciones
          </a>
        </div>
      )}

      {/* ---------- Toolbar ---------- */}
      <div className="al-cli-toolbar">
        <div className="al-cli-search">
          <Icon name="search" size={14} />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Buscar por nombre o email…"
          />
          {qInput && (
            <button
              className="al-cli-search-clear"
              onClick={() => setQInput("")}
              title="Limpiar búsqueda"
              aria-label="Limpiar"
            >
              <Icon name="x" size={12} />
            </button>
          )}
        </div>
        <StatusTabs value={status} onChange={setStatus} />
      </div>

      {/* ---------- Grid / Empty ---------- */}
      {isEmpty ? (
        <div className="card" style={{ padding: 32 }}>
          {isFiltered ? (
            <EmptyState
              icon="search"
              title="Sin resultados"
              body={
                qDebounced
                  ? `No encontré clientes que coincidan con "${qDebounced}".`
                  : status === "archived"
                  ? "Aún no has archivado clientes."
                  : "No hay clientes en este filtro."
              }
              action={
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setQInput("");
                    setStatus("active");
                  }}
                >
                  Limpiar filtros
                </button>
              }
            />
          ) : (
            <EmptyState
              icon="user"
              title="Crea tu primer cliente"
              body="Tus clientes son la base: les vinculas planes, medidas y notas. Cada uno hereda automáticamente sus datos al editor y a la calculadora."
              action={
                <button
                  className="btn btn-primary"
                  onClick={openCreate}
                  disabled={limitReached}
                >
                  <Icon name="plus" size={14} /> Crear cliente
                </button>
              }
            />
          )}
        </div>
      ) : (
        <div className="al-cli-grid">
          {clients!.map((c) => (
            <ClientCard
              key={c.id}
              client={c}
              onEdit={() => openEdit(c)}
              onArchive={() => handleArchive(c)}
              onRestore={() => handleRestore(c)}
              onViewPlans={() => go("dashboard", `client_id=${c.id}`)}
              onCreatePlan={() => setCreatingPlanFor(c)}
            />
          ))}
        </div>
      )}

      {/* ---------- Modal "Crear plan" (unificado) ---------- */}
      {creatingPlanFor && (
        <CreatePlanModal
          lockedClient={creatingPlanFor}
          onClose={() => setCreatingPlanFor(null)}
          onCreated={(planId) => {
            setCreatingPlanFor(null);
            go("plan-builder", `id=${planId}`);
          }}
        />
      )}

      {/* ---------- Modal ---------- */}
      {modalMode && (
        <ClientFormModal
          mode={modalMode}
          client={editing || undefined}
          onClose={() => {
            setModalMode(null);
            setEditing(null);
          }}
          onSaved={async (saved) => {
            setModalMode(null);
            setEditing(null);
            // Refrescamos la lista + usage (el cap puede haber cambiado)
            await Promise.all([load(), refreshUser()]);
            // Si saved no está en el filtro actual (ej: archivamos un activo
            // mientras estamos en tab "Activos"), no hacemos nada extra —
            // la lista re-cargada ya refleja el estado correcto.
            void saved;
          }}
        />
      )}
    </div>
  );
}

// ---------------- StatusTabs ----------------

function StatusTabs({
  value,
  onChange,
}: {
  value: ClientStatusFilter;
  onChange: (v: ClientStatusFilter) => void;
}) {
  const TABS: { id: ClientStatusFilter; label: string }[] = [
    { id: "active", label: "Activos" },
    { id: "archived", label: "Archivados" },
    { id: "all", label: "Todos" },
  ];
  return (
    <div className="al-cli-tabs" role="tablist">
      {TABS.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={value === t.id}
          className={"al-cli-tab " + (value === t.id ? "is-active" : "")}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------------- ClientCard ----------------

interface ClientCardProps {
  client: ClientFull;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onViewPlans: () => void;
  onCreatePlan: () => void;
}

function ClientCard({ client, onEdit, onArchive, onRestore, onViewPlans, onCreatePlan }: ClientCardProps) {
  const archived = !client.is_active;
  const initials = initialsOf(client.full_name);
  const color = colorFor(client.full_name);

  return (
    <div className={"card al-cli-card " + (archived ? "is-archived" : "")}>
      <div className="al-cli-card-top">
        {client.photo_url ? (
          <img
            src={client.photo_url}
            alt={client.full_name}
            className="al-cli-photo"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <Avatar user={{ initials, avatar_color: color }} size="lg" />
        )}
        <div className="al-cli-card-info">
          <div className="al-cli-card-name">
            {client.full_name}
            {archived && <span className="al-cli-archived-badge">archivado</span>}
          </div>
          <div className="tiny muted">
            {client.email || client.phone || "Sin contacto"}
          </div>
          <div className="tiny muted">
            {client.plans_count} {client.plans_count === 1 ? "plan" : "planes"} ·
            actualizado {relativeTime(client.updated_at)}
          </div>
        </div>
      </div>

      <div className="al-cli-card-actions">
        <button className="btn btn-secondary btn-sm" onClick={onEdit} title="Editar perfil">
          <Icon name="settings" size={12} /> Editar
        </button>
        {!archived && (
          <button
            className="btn btn-primary btn-sm"
            onClick={onCreatePlan}
            title="Crear un plan nuevo con los datos de este cliente precargados"
          >
            <Icon name="plus" size={12} /> Crear plan
          </button>
        )}
        {archived ? (
          <button className="btn btn-ghost btn-sm" onClick={onRestore} title="Restaurar (cuenta para el cap)">
            Restaurar
          </button>
        ) : (
          <button className="btn btn-ghost btn-sm" onClick={onArchive} title="Archivar (libera cuota)">
            Archivar
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={onViewPlans} title="Ver planes vinculados">
          Planes <Icon name="arrow" size={12} />
        </button>
      </div>
    </div>
  );
}
